import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type RefObject,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import {
  mentionedPromptImageTags,
  promptImageTagQueryAtCaret,
} from "@shared/promptImageTags";

/**
 * "@image1" autocomplete for the video composer prompt (PixVerse-style).
 * Every reference chip carries a tag; typing "@" in the prompt opens this
 * menu, picking an entry inserts "@imageN " at the caret, and the sentence
 * around the handle becomes that photo's instruction on the server (see
 * shared/promptImageTags.ts). Pure UI — no network, no persistence.
 */

/** The prompt element the menu is bound to: the composer's single-line
 *  <input> or a <textarea>. */
export type PromptImageTagField = HTMLTextAreaElement | HTMLInputElement;

export type PromptImageTagOption = {
  tag: string;
  imageUrl: string;
  name: string;
  role: "character" | "vibe";
  isLead: boolean;
};

export type PromptImageTagPalette = {
  panel: string;
  border: string;
  fg: string;
  dim: string;
  accentSoft: string;
  accentBorder: string;
  mono?: string;
};

const DIRECTOR_PALETTE: PromptImageTagPalette = {
  panel: "var(--panel-2, #16161c)",
  border: "var(--line-2, rgba(255,255,255,0.12))",
  fg: "var(--fg, #f4f4f8)",
  dim: "var(--fg-dim, rgba(244,244,248,0.6))",
  accentSoft: "var(--accent-soft, rgba(124,93,250,0.18))",
  accentBorder: "rgba(167,139,250,0.45)",
  mono: "var(--mono, ui-monospace, monospace)",
};

export function usePromptImageTagMenu({
  textareaRef,
  value,
  onChange,
  options,
  palette = DIRECTOR_PALETTE,
}: {
  textareaRef: RefObject<PromptImageTagField | null>;
  value: string;
  onChange: (next: string) => void;
  options: PromptImageTagOption[];
  palette?: PromptImageTagPalette;
}): {
  open: boolean;
  menu: ReactNode;
  textareaProps: {
    onKeyDown: (event: KeyboardEvent<PromptImageTagField>) => void;
    onKeyUp: () => void;
    onClick: () => void;
    onBlur: () => void;
  };
} {
  const [query, setQuery] = useState<
    { start: number; query: string } | undefined
  >(undefined);
  const [activeIndex, setActiveIndex] = useState(0);
  const closeTimerRef = useRef<number | null>(null);

  const refresh = useCallback(() => {
    const el = textareaRef.current;
    if (!el || options.length === 0) {
      setQuery(undefined);
      return;
    }
    const caret = el.selectionStart ?? el.value.length;
    const next = promptImageTagQueryAtCaret(el.value, caret);
    setQuery((prev) =>
      prev?.start === next?.start && prev?.query === next?.query ? prev : next,
    );
  }, [options.length, textareaRef]);

  // The caret moves with every value change; recompute after React commits
  // the new value so selectionStart reflects it.
  useEffect(() => {
    refresh();
  }, [value, refresh]);

  const matches = useMemo(() => {
    if (!query) return [];
    const q = query.query;
    return options.filter(
      (option) =>
        !q ||
        option.tag.startsWith(q) ||
        option.tag.replace(/^image/, "").startsWith(q) ||
        option.name.toLowerCase().includes(q),
    );
  }, [options, query]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query?.start, query?.query]);

  const open = !!query && matches.length > 0;

  const insert = useCallback(
    (option: PromptImageTagOption) => {
      const el = textareaRef.current;
      if (!el || !query) return;
      const caret = el.selectionStart ?? el.value.length;
      const head = el.value.slice(0, query.start);
      const tail = el.value.slice(caret);
      const token = `@${option.tag}`;
      const spacer = tail.startsWith(" ") || tail === "" ? " " : " ";
      const next = `${head}${token}${spacer}${tail.replace(/^ /, "")}`;
      const pos = head.length + token.length + spacer.length;
      onChange(next);
      setQuery(undefined);
      requestAnimationFrame(() => {
        const node = textareaRef.current;
        if (!node) return;
        node.focus();
        try {
          node.setSelectionRange(pos, pos);
        } catch {}
      });
    },
    [onChange, query, textareaRef],
  );

  const onKeyDown = useCallback(
    (event: KeyboardEvent<PromptImageTagField>) => {
      if (!open) return;
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setActiveIndex((i) => (i + 1) % matches.length);
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        setActiveIndex((i) => (i - 1 + matches.length) % matches.length);
      } else if (event.key === "Enter" || event.key === "Tab") {
        event.preventDefault();
        insert(matches[Math.min(activeIndex, matches.length - 1)]);
      } else if (event.key === "Escape") {
        event.preventDefault();
        setQuery(undefined);
      }
    },
    [activeIndex, insert, matches, open],
  );

  const onBlur = useCallback(() => {
    // Let a click on a menu row land before the menu goes away.
    closeTimerRef.current = window.setTimeout(() => setQuery(undefined), 120);
  }, []);

  useEffect(
    () => () => {
      if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
    },
    [],
  );

  const menu = open ? (
    <PromptImageTagMenu
      options={matches}
      activeIndex={activeIndex}
      onHover={setActiveIndex}
      onPick={insert}
      palette={palette}
    />
  ) : null;

  return {
    open,
    menu,
    textareaProps: { onKeyDown, onKeyUp: refresh, onClick: refresh, onBlur },
  };
}

function PromptImageTagMenu({
  options,
  activeIndex,
  onHover,
  onPick,
  palette,
}: {
  options: PromptImageTagOption[];
  activeIndex: number;
  onHover: (index: number) => void;
  onPick: (option: PromptImageTagOption) => void;
  palette: PromptImageTagPalette;
}) {
  const { t } = useTranslation();
  return (
    <div
      role="listbox"
      aria-label={t("promptImageTags.menuLabel", {
        defaultValue: "Tag a photo",
      })}
      style={{
        position: "absolute",
        left: 0,
        top: "100%",
        zIndex: 40,
        minWidth: 260,
        maxWidth: "100%",
        marginTop: 4,
        padding: 6,
        borderRadius: 12,
        background: palette.panel,
        border: `1px solid ${palette.border}`,
        boxShadow: "0 12px 32px rgba(0,0,0,0.35)",
      }}
    >
      <div
        style={{
          fontSize: 11,
          color: palette.dim,
          padding: "2px 8px 6px",
          fontFamily: palette.mono,
        }}
      >
        {t("promptImageTags.menuHint", {
          defaultValue: "Insert a photo tag, then say how it's used",
        })}
      </div>
      {options.map((option, index) => {
        const active = index === activeIndex;
        return (
          <button
            key={option.tag}
            type="button"
            role="option"
            aria-selected={active}
            onMouseDown={(event) => event.preventDefault()}
            onMouseEnter={() => onHover(index)}
            onClick={() => onPick(option)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              width: "100%",
              padding: "6px 8px",
              borderRadius: 8,
              border: `1px solid ${active ? palette.accentBorder : "transparent"}`,
              background: active ? palette.accentSoft : "transparent",
              color: palette.fg,
              cursor: "pointer",
              textAlign: "left",
            }}
          >
            <img
              src={option.imageUrl}
              alt=""
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                objectFit: "cover",
                flexShrink: 0,
              }}
            />
            <span
              style={{ display: "flex", flexDirection: "column", minWidth: 0 }}
            >
              <span
                style={{
                  fontFamily: palette.mono,
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                @{option.tag}
              </span>
              <span
                style={{
                  fontSize: 11.5,
                  color: palette.dim,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {option.isLead
                  ? t("promptImageTags.roleLead", {
                      defaultValue: "Lead singer · {{name}}",
                      name: option.name,
                    })
                  : option.role === "vibe"
                    ? t("promptImageTags.roleVibe", {
                        defaultValue: "Vibe · {{name}}",
                        name: option.name,
                      })
                    : t("promptImageTags.roleCharacter", {
                        defaultValue: "Character · {{name}}",
                        name: option.name,
                      })}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * One-line nudge under the prompt once photos are attached but none is
 * tagged yet. Disappears as soon as the prompt mentions a tag.
 */
export function PromptImageTagHint({
  options,
  prompt,
  style,
  className,
}: {
  options: PromptImageTagOption[];
  prompt: string;
  style?: CSSProperties;
  className?: string;
}) {
  const { t } = useTranslation();
  if (options.length === 0) return null;
  if (mentionedPromptImageTags(prompt).length > 0) return null;
  const first = options[0]?.tag ?? "image1";
  const second = options[1]?.tag;
  return (
    <div className={className} style={{ fontSize: 12, lineHeight: 1.5, ...style }}>
      {second
        ? t("promptImageTags.hintTwo", {
            defaultValue:
              'Tip: type @ to tag a photo and say how it\'s used, e.g. "@{{first}} is the singer, @{{second}} is the location".',
            first,
            second,
          })
        : t("promptImageTags.hintOne", {
            defaultValue:
              'Tip: type @ to tag your photo and say how it\'s used, e.g. "@{{first}} sings the whole song".',
            first,
          })}
    </div>
  );
}
